import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { toast } from "react-toastify";
import useSettings from "../../hooks/useSettings";
import { i18n } from "../../translate/i18n";

const CLIENTE_FIELD_REQUIREMENTS_SETTING_KEY = "clienteFieldRequirementsV1";

const DEFAULT_FIELD_REQUIREMENTS = {
  cliente: {
    NOMECLI: true,
    NOMEFANTASIA: false,
    PESSOA: true,
    STATUS: false,
    USUARIO_RESPONSAVEL: false,
    INSCESTCLI: false,
    insc_municipal: false,
    suframa: false,
    contribuinte_icms: false,
    DDDCLI: false,
    E_MAIL: false,
    FONECLI: false,
    DDDCELULAR: false,
    CELULAR: false,
    site: false,
    CEPCLI: false,
    ENDCLI: false,
    NUMERO: false,
    BAIRROCLI: false,
    CIDADECLI: false,
    ESTADOCLI: false,
    LIMITE: false,
    metas_vendas: false,
    potencial_vendas: false,
    acoes_vendas: false,
    OBS: false,
    CGCCLI: false,
    CPFCLI: false,
  },
  contacts: {
    name: true,
    number: true,
    email: false,
  },
};

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  helperText: {
    marginBottom: theme.spacing(2),
  },
}));

const CLIENTE_FIELDS = [
  { key: "NOMECLI", labelKey: "settings.requiredFields.fields.cliente.NOMECLI" },
  { key: "NOMEFANTASIA", labelKey: "settings.requiredFields.fields.cliente.NOMEFANTASIA" },
  { key: "PESSOA", labelKey: "settings.requiredFields.fields.cliente.PESSOA" },
  { key: "STATUS", labelKey: "settings.requiredFields.fields.cliente.STATUS" },
  { key: "USUARIO_RESPONSAVEL", labelKey: "settings.requiredFields.fields.cliente.USUARIO_RESPONSAVEL" },
  { key: "INSCESTCLI", labelKey: "settings.requiredFields.fields.cliente.INSCESTCLI" },
  { key: "insc_municipal", labelKey: "settings.requiredFields.fields.cliente.insc_municipal" },
  { key: "suframa", labelKey: "settings.requiredFields.fields.cliente.suframa" },
  { key: "contribuinte_icms", labelKey: "settings.requiredFields.fields.cliente.contribuinte_icms" },
  { key: "DDDCLI", labelKey: "settings.requiredFields.fields.cliente.DDDCLI" },
  { key: "E_MAIL", labelKey: "settings.requiredFields.fields.cliente.E_MAIL" },
  { key: "FONECLI", labelKey: "settings.requiredFields.fields.cliente.FONECLI" },
  { key: "DDDCELULAR", labelKey: "settings.requiredFields.fields.cliente.DDDCELULAR" },
  { key: "CELULAR", labelKey: "settings.requiredFields.fields.cliente.CELULAR" },
  { key: "site", labelKey: "settings.requiredFields.fields.cliente.site" },
  { key: "CEPCLI", labelKey: "settings.requiredFields.fields.cliente.CEPCLI" },
  { key: "ENDCLI", labelKey: "settings.requiredFields.fields.cliente.ENDCLI" },
  { key: "NUMERO", labelKey: "settings.requiredFields.fields.cliente.NUMERO" },
  { key: "BAIRROCLI", labelKey: "settings.requiredFields.fields.cliente.BAIRROCLI" },
  { key: "CIDADECLI", labelKey: "settings.requiredFields.fields.cliente.CIDADECLI" },
  { key: "ESTADOCLI", labelKey: "settings.requiredFields.fields.cliente.ESTADOCLI" },
  { key: "LIMITE", labelKey: "settings.requiredFields.fields.cliente.LIMITE" },
  { key: "metas_vendas", labelKey: "settings.requiredFields.fields.cliente.metas_vendas" },
  { key: "potencial_vendas", labelKey: "settings.requiredFields.fields.cliente.potencial_vendas" },
  { key: "acoes_vendas", labelKey: "settings.requiredFields.fields.cliente.acoes_vendas" },
  { key: "OBS", labelKey: "settings.requiredFields.fields.cliente.OBS" },
  { key: "CGCCLI", labelKey: "settings.requiredFields.fields.cliente.CGCCLI" },
  { key: "CPFCLI", labelKey: "settings.requiredFields.fields.cliente.CPFCLI" },
];

const CONTATO_FIELDS = [
  { key: "name", labelKey: "settings.requiredFields.fields.contacts.name" },
  { key: "number", labelKey: "settings.requiredFields.fields.contacts.number" },
  { key: "email", labelKey: "settings.requiredFields.fields.contacts.email" },
];

const ClienteFieldRequirementsSettings = ({ settings = [] }) => {
  const classes = useStyles();
  const { update } = useSettings();
  const [saving, setSaving] = useState(false);
  const [requirements, setRequirements] = useState(DEFAULT_FIELD_REQUIREMENTS);

  useEffect(() => {
    const setting = settings.find(
      (item) => item.key === CLIENTE_FIELD_REQUIREMENTS_SETTING_KEY
    );

    if (!setting?.value) {
      setRequirements(DEFAULT_FIELD_REQUIREMENTS);
      return;
    }

    try {
      const parsed = JSON.parse(setting.value);
      setRequirements({
        cliente: {
          ...DEFAULT_FIELD_REQUIREMENTS.cliente,
          ...(parsed?.cliente || {}),
        },
        contacts: {
          ...DEFAULT_FIELD_REQUIREMENTS.contacts,
          ...(parsed?.contacts || {}),
        },
      });
    } catch (error) {
      setRequirements(DEFAULT_FIELD_REQUIREMENTS);
    }
  }, [settings]);

  const handleToggle = (group, field) => (event) => {
    const checked = Boolean(event.target.checked);
    setRequirements((prevState) => ({
      ...prevState,
      [group]: {
        ...prevState[group],
        [field]: checked,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await update({
        key: CLIENTE_FIELD_REQUIREMENTS_SETTING_KEY,
        value: JSON.stringify(requirements),
      });
      toast.success(i18n.t("settings.requiredFields.messages.saved"));
    } catch (error) {
      toast.error(i18n.t("settings.requiredFields.messages.error"));
    }
    setSaving(false);
  };

  return (
    <Box>
      <Typography variant="h6" className={classes.sectionTitle}>
        {i18n.t("settings.requiredFields.title")}
      </Typography>
      <Typography variant="body2" color="textSecondary" className={classes.helperText}>
        {i18n.t("settings.requiredFields.description")}
      </Typography>

      <Paper className={classes.section} variant="outlined">
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          {i18n.t("settings.requiredFields.sections.cliente")}
        </Typography>
        <Grid container>
          {CLIENTE_FIELDS.map((field) => (
            <Grid key={field.key} item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={Boolean(requirements.cliente[field.key])}
                    onChange={handleToggle("cliente", field.key)}
                  />
                }
                label={i18n.t(field.labelKey)}
              />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper className={classes.section} variant="outlined">
        <Typography variant="subtitle1" className={classes.sectionTitle}>
          {i18n.t("settings.requiredFields.sections.contacts")}
        </Typography>
        <Grid container>
          {CONTATO_FIELDS.map((field) => (
            <Grid key={field.key} item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={Boolean(requirements.contacts[field.key])}
                    onChange={handleToggle("contacts", field.key)}
                  />
                }
                label={i18n.t(field.labelKey)}
              />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Button
        variant="contained"
        color="primary"
        onClick={handleSave}
        disabled={saving}
      >
        {saving
          ? i18n.t("settings.requiredFields.buttons.saving")
          : i18n.t("settings.requiredFields.buttons.save")}
      </Button>
    </Box>
  );
};

export default ClienteFieldRequirementsSettings;
